import React, {ReactElement} from "react";
import {graphql, PageProps} from "gatsby";

import Layout from "/src/components/layouts/layout/Layout";
import BlogHero from "/src/components/hero-side-by-side-module/blog-hero/BlogHero";
import HeaderInfo from "/src/components/header-info/HeaderInfo";
import FlexibleBaseContentModel, {
  FlexibleBaseContentModelProps,
} from "/src/components/flexible-base-content-model/FlexibleBaseContentModel";
import Quotes, {parseContentfulQuoteData} from "/src/components/quotes/Quotes";
import Image, {ImageObject} from "/src/components/image/Image";
import {CTA} from "/src/components/buttons/cta-button/CTAButton";
import ModuleBody from "/src/components/layouts/module-body/ModuleBody";
import WidgetShare from "/src/components/widgets/widget-share/WidgetShare";
import BreadcrumbsShareCardLayout from "/src/components/layouts/breadcrumbs-share-card-layout/BreadcrumbsShareCardLayout";
import {CardProps} from "/src/components/cards/card/Card";
import CardLayout from "/src/components/layouts/card-layout/CardLayout";
import LinkTag from "/src/components/buttons/link-tag/LinkTag";
import Icon from "/src/components/icon/Icon";
import {GetStartedModuleProps} from "/src/components/get-started-module/GetStartedModule";

import {
  narrowConnection,
  css,
  generateKeyId,
  constructArticleTemplatePageURL,
} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {formattedPublishDate} from "/src/utils/blog";
import buildGetStartedModuleFragmentFragment from "/src/contentful/fixture-builders/get-started-module";

import ChevronArrowIcon from "/src/svg/chevron-arrow.svg";

import * as styles from "./blog-post.module.scss";

interface BlogPostTemplateAuthorFooterTileProps {
  image: ImageObject;
  url: string;
  cta?: CTA | null;
  copy: string;
}

const AuthorFooterTile = (
  props: BlogPostTemplateAuthorFooterTileProps
): ReactElement => {
  const {url, image, cta, copy} = props;
  return (
    <div className={styles.blogPostTemplateAuthorTile}>
      {image && (
        <LinkTag
          classes={styles.blogPostTemplateAuthorTile__imgLink}
          href={url}
          ariaLabel={copy}
        >
          <Image
            objectFit="cover"
            image={image}
            classes={styles.blogPostTemplateAuthorTile__img}
          />
        </LinkTag>
      )}

      <div>
        <LinkTag
          href={url}
          classes={css("p", styles.blogPostTemplateAuthorTile__link)}
        >
          {copy}
        </LinkTag>

        {cta && (
          <LinkTag
            href={cta.url}
            classes={css(
              "p",
              styles.blogPostTemplateAuthorTile__link,
              styles.blogPostTemplateAuthorTile__linkSocial
            )}
          >
            <span>{cta.label}</span>
            <Icon role="presentation" svg={ChevronArrowIcon} />
          </LinkTag>
        )}
      </div>
    </div>
  );
};

const BlogPostTemplate = ({
  data,
}: PageProps<Queries.BlogPostTemplateQueryQuery>): ReactElement => {
  const {
    contentfulPromoBanner,
    contentfulBlogPost,
    authorBlogPosts,
    allContentfulBlogPost,
  } = data;

  const {
    slug,
    mastheadImage,
    metaDescription,
    title = "",
    publishedAt,
    author,
    body,
    contentModules,
    categories,
    type,
    form,
    flexibleBaseContent,
  } = contentfulBlogPost || {};

  const postsByAuthor = narrowConnection(authorBlogPosts).map(post => {
    const url = constructArticleTemplatePageURL(post.slug, post.type);
    return {
      image: post.mastheadImage,
      title: post.title,
      url,
    };
  });

  const moreBlogPosts = narrowConnection(allContentfulBlogPost);

  const moreBlogCards = moreBlogPosts.map(blogPost => {
    const {title, mastheadImage, slug, type} = blogPost;

    if (slug == null) {
      return null;
    }

    return {
      eyebrow: translate("Blog"),
      headline: title,
      image: mastheadImage,
      ctas: [
        {
          label: translate("Read More"),
          url: constructArticleTemplatePageURL(slug, type),
          amplitudeCtaProps: {
            name: "blog_post_more_articles",
            type: "link",
            linkUrl: constructArticleTemplatePageURL(slug, type),
            contentType: "blogPost",
          },
        },
      ],
    };
  }) as Array<CardProps>;

  const quoteModules = (contentModules || []).filter(
    item => item && item.__typename === "ContentfulQuoteModule"
  );

  const quoteModule = quoteModules.length !== 0 ? quoteModules[0] : null;

  const getStartedModules = (contentModules || []).filter(
    item => item && item.__typename === "ContentfulGetStartedModule"
  );

  const getStartedModule =
    getStartedModules.length !== 0 ? getStartedModules[0] : null;

  const flexibleBaseContentContentModules = (contentModules || []).filter(
    item =>
      item &&
      item.__typename !== "ContentfulQuoteModule" &&
      item.__typename !== "ContentfulGetStartedModule"
  );

  // let's construct legacy data and use latest flexible base content model component
  let flexibleBaseContentData: Record<string, any> = {
    slug: slug!,
    skipHeaderTags: true,
    metaDescription,
    contentModules: flexibleBaseContentContentModules || null,
    title,
    author:
      author as Queries.FlexibleBaseContentModelFragmentFragment["author"],
    showSocialMediaIcons: author?.showSocialMediaIcons,
    publishDate: publishedAt,
    socialMediaImage: mastheadImage,
    articleType: type,
    categoryTag: categories ? categories[0] : null,
    rightSectionWidgets: [],
  };

  if (flexibleBaseContent) {
    flexibleBaseContentData = {
      ...flexibleBaseContentData,
      ...flexibleBaseContent,
    };
  } else {
    if (body?.body) {
      // legacy blog posts only have a `body`
      flexibleBaseContentData.contentModules = [
        {
          __typename: "ContentfulContentPageBodyCopy",
          body: body,
        },
        ...(contentModules || []).filter(
          item => item?.__typename !== "ContentfulContentPageBodyCopy"
        ),
      ];

      // add get started module
      const legacyGetStartedModule = buildGetStartedModuleFragmentFragment({
        layout: "plain",
        title: translate("Ready to Get Started?"),
        body: {
          body: translate(
            "Flexport makes shipping your cargo transparent, reliable, and affordable"
          ),
        },
        darkMode: false,
        ctaButtons: [
          {
            __typename: "ContentfulCtaButton",
            label: translate("Get Started"),
            url: "/sign-up",
            amplitudeCtaType: "sign_up",
            amplitudeCtaName: "blog-get-started-cta",
            openInNewTab: false,
          },
        ],
      }) as GetStartedModuleProps;

      flexibleBaseContentData.rightSectionWidgets.push(legacyGetStartedModule);
    }

    if (form) {
      flexibleBaseContentData.rightSectionWidgets.push(form);
    }

    if (getStartedModule) {
      flexibleBaseContentData.rightSectionWidgets.push(getStartedModule);
    }
  }

  return (
    <Layout promoBannerData={contentfulPromoBanner}>
      <HeaderInfo
        title={title || flexibleBaseContent?.title!}
        description={metaDescription || flexibleBaseContent?.metaDescription!}
        image={
          (mastheadImage ||
            flexibleBaseContent?.socialMediaImage) as ImageObject
        }
      />

      <BlogHero
        type={type || "blog"}
        eyebrow={formattedPublishDate(publishedAt!)!}
        title={title || flexibleBaseContent?.title!}
        categories={categories}
        author={author}
        bgImage={mastheadImage as ImageObject}
        showSocialIcons
        showBackBtn
        slug={slug!}
      />

      {flexibleBaseContentData && (
        <FlexibleBaseContentModel
          {...(flexibleBaseContentData as FlexibleBaseContentModelProps)}
        />
      )}

      {quoteModule && (
        <Quotes
          {...parseContentfulQuoteData(
            quoteModule as Queries.QuoteModuleFragmentFragment
          )}
        />
      )}

      <div className={styles.blogPostTemplate__share}>
        <ModuleBody>
          <WidgetShare showTitle />
        </ModuleBody>
      </div>

      <div className={styles.blogPostTemplate__authorPosts}>
        <ModuleBody>
          <p
            className={css(
              "eyebrow",
              styles.blogPostTemplate__authorPosts__headline
            )}
          >
            {translate("About this author")}
          </p>

          <CardLayout
            classes={styles.blogPostTemplate__authorPosts__tiles}
            column="3"
            items={[
              <AuthorFooterTile
                key={`blog-post-template-author-tile-author-${author?.name}`}
                copy={author?.name!}
                url={author?.slug ? `/blog/author/${author?.slug}` : "/blog"}
                cta={
                  author?.twitterHandle
                    ? {
                        label: translate("Follow on Twitter"),
                        url: `https://www.twitter.com/${author?.twitterHandle}`,
                      }
                    : null
                }
                image={author?.headshot as ImageObject}
              />,
              ...(postsByAuthor || []).map((item, i) => {
                const key = generateKeyId(
                  `blog-post-template-author-tile-${i}`
                );

                return (
                  <AuthorFooterTile
                    key={key}
                    copy={item.title!}
                    url={item.url}
                    image={item.image as ImageObject}
                  />
                );
              }),
            ]}
          />
        </ModuleBody>
      </div>

      <div className={styles.blogPostTemplate__more}>
        <BreadcrumbsShareCardLayout
          cards={moreBlogCards}
          cardImageClasses={styles.blogPostTemplate__more__cardImage}
          headline={translate("More from Flexport")}
        />
      </div>
    </Layout>
  );
};

export default BlogPostTemplate;

export const query = graphql`
  query BlogPostTemplateQuery(
    $slug: String!
    $locale: String!
    $authorSlug: String!
    $localeBasedPageFilter: [String]
  ) {
    contentfulPromoBanner(
      node_locale: {eq: $locale}
      contentful_id: {eq: "17z8sGPISXmsBtu2jynvvp"}
    ) {
      ...PromoBannerFragment
    }
    contentfulBlogPost(slug: {eq: $slug}, node_locale: {eq: $locale}) {
      type
      mastheadImage {
        ... on ContentfulAsset {
          ...AssetFragment
        }
        gatsbyImageData
      }
      title
      slug
      publishedAt
      body {
        body
      }
      categories {
        title
        slug
      }
      author {
        name
        slug
        title
        twitterHandle
        bio {
          bio
        }
        headshot {
          ... on ContentfulAsset {
            ...AssetFragment
          }
          gatsbyImageData
        }
        showSocialMediaIcons
      }
      metaDescription
      form {
        ...SimpleFormFragment
      }
      flexibleBaseContent {
        ...FlexibleBaseContentModelFragment
      }
      contentModules {
        __typename
        ... on Node {
          ... on ContentfulContentPageBodyCopy {
            body {
              body
            }
          }
          ... on ContentfulContentPageVideo {
            videoSource
            videoControls
            popup
            autoplay
            caption {
              caption
            }
          }
          ... on ContentfulContentPagePullQuote {
            quoteText {
              quoteText
            }
            authorName
            authorTitle
          }
          ... on ContentfulCallout {
            ...CalloutFragment
          }
          ... on ContentfulGetStartedModule {
            ...GetStartedModuleFragment
          }
          ... on ContentfulQuoteModule {
            ...QuoteModuleFragment
          }
        }
      }
    }
    allContentfulBlogPost(
      filter: {
        node_locale: {eq: $locale}
        slug: {ne: $slug}
        onlyPublishTo: {in: $localeBasedPageFilter}
      }
      limit: 3
    ) {
      edges {
        node {
          id
          slug
          title
          type
          mastheadImage {
            ... on ContentfulAsset {
              ...AssetFragment
            }
            gatsbyImageData
          }
        }
      }
    }
    authorBlogPosts: allContentfulBlogPost(
      filter: {
        node_locale: {eq: $locale}
        slug: {ne: $slug}
        author: {slug: {eq: $authorSlug}}
        onlyPublishTo: {in: $localeBasedPageFilter}
      }
      limit: 2
    ) {
      edges {
        node {
          id
          slug
          title
          type
          mastheadImage {
            ... on ContentfulAsset {
              ...AssetFragment
            }
            gatsbyImageData
          }
        }
      }
    }
  }
`;
